import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Trophy, Sparkles, PartyPopper, ArrowRight } from "lucide-react";
import { API } from "@/App";
import { fireConfetti } from "@/lib/confetti";
import { WHATSAPP_SALES_URL } from "@/lib/sales";

/**
 * Sweet, celebratory admin-editable Congratulations banner.
 * Renders between the Hero and Weekly Promo cards on the homepage.
 * Toggle enabled/disabled and edit copy via Admin → Content Settings.
 */
const CongratsBanner = () => {
  const [c, setC] = useState(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/congrats-banner`)
      .then((r) => setC(r.data))
      .catch(() => setC(null));
  }, []);

  if (!c || !c.enabled || !(c.title || c.message)) return null;

  const go = (link, el) => {
    if (el) fireConfetti(el);
    axios.post(`${API}/congrats/track`, { event: "banner_click" }).catch(() => {});
    // All subscription CTAs now funnel buyers to Madam Precious on WhatsApp.
    setTimeout(() => {
      const target = /^https?:\/\//i.test(link || "") ? link : WHATSAPP_SALES_URL;
      window.open(target, "_blank", "noopener,noreferrer");
    }, 250);
  };

  return (
    <section className="py-8 px-4" data-testid="congrats-banner">
      <div className="max-w-5xl mx-auto">
        <div
          className="relative overflow-hidden rounded-3xl border-2 border-amber-300 shadow-[0_20px_50px_-12px_rgba(251,191,36,0.35)]"
          style={{
            background:
              "linear-gradient(135deg, #052e16 0%, #065f46 40%, #047857 65%, #b45309 100%)",
          }}
        >
          {/* Confetti dots overlay */}
          <div className="pointer-events-none absolute inset-0 opacity-60">
            {[
              { l: "8%", t: "18%", c: "#fbbf24" },
              { l: "22%", t: "72%", c: "#f472b6" },
              { l: "38%", t: "28%", c: "#34d399" },
              { l: "55%", t: "80%", c: "#fbbf24" },
              { l: "70%", t: "22%", c: "#60a5fa" },
              { l: "85%", t: "60%", c: "#f472b6" },
              { l: "92%", t: "16%", c: "#34d399" },
              { l: "14%", t: "50%", c: "#f97316" },
            ].map((d, i) => (
              <span
                key={i}
                className="absolute h-2 w-2 rounded-full animate-pulse"
                style={{ left: d.l, top: d.t, background: d.c, animationDelay: `${i * 200}ms` }}
              />
            ))}
          </div>

          {/* Radial glow */}
          <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-amber-400/25 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-emerald-400/20 blur-3xl" />

          <div className="relative z-10 grid md:grid-cols-[auto_1fr_auto] items-center gap-6 px-6 py-8 sm:px-10 sm:py-10">
            {/* Trophy medal */}
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-amber-400 blur-xl opacity-60 animate-pulse" />
                <div className="relative h-20 w-20 rounded-full bg-gradient-to-br from-amber-300 via-amber-500 to-amber-700 flex items-center justify-center shadow-inner">
                  <Trophy className="h-10 w-10 text-white drop-shadow" />
                </div>
                <Sparkles className="absolute -top-2 -right-2 h-6 w-6 text-amber-200 animate-pulse" />
              </div>
            </div>

            {/* Copy */}
            <div className="text-center md:text-left">
              {c.highlight ? (
                <span
                  className="inline-flex items-center gap-1.5 text-[11px] sm:text-xs font-extrabold tracking-widest uppercase text-amber-200 bg-amber-500/10 border border-amber-300/40 rounded-full px-3 py-1 mb-3"
                  data-testid="congrats-highlight"
                >
                  <PartyPopper className="h-3.5 w-3.5" />
                  {c.highlight}
                </span>
              ) : null}
              <h2
                className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-tight mb-2"
                style={{ textShadow: "0 2px 10px rgba(0,0,0,0.35)" }}
                data-testid="congrats-title"
              >
                {c.title}
              </h2>
              {c.message ? (
                <p
                  className="text-sm sm:text-base text-emerald-50/90 max-w-2xl whitespace-pre-line leading-relaxed"
                  data-testid="congrats-message"
                >
                  {c.message}
                </p>
              ) : null}
            </div>

            {/* CTA */}
            {c.cta_text ? (
              <button
                ref={ctaRef}
                onClick={() => go(c.cta_link || "whatsapp", ctaRef.current)}
                className="group inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-emerald-950 font-extrabold px-6 py-3 shadow-lg shadow-amber-500/30 transition-transform hover:scale-[1.03] active:scale-100"
                data-testid="congrats-cta"
              >
                {c.cta_text}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CongratsBanner;
